import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { InputAdornment, TextField } from '@mui/material';

import { useCartStyles } from '../hooks/useCartStyles';
import { useDefineUserRole, useFormatMessage } from '../../../../hooks';
import { selectIsAuthorized, selectMe } from '../selectors';
import { ButtonWithLoader } from '../../../../components/other/Buttons';
import { useFormikCompany } from '../hooks/useFormikCompany';
import { useAxiosGetCallback } from '../../../../hooks/useAxiosGet';
import { useHandleCountrySelect } from '../../../auth/hooks/useHandleCountrySelect';
import CountrySelect from '../../../../components/ui/CountrySelect';
import { IMaskPhoneInput } from '../../../auth/components/CheckInSystem';

export function CompanyForm() {
  const classes = useCartStyles();
  const fm = useFormatMessage();
  const {
    data: countries,
    loading: countriesLoading,
    makeRequest,
  } = useAxiosGetCallback<any>();
  const { countryId, setCountryId, countryData } = useHandleCountrySelect(countries, 1);
  const me = useSelector(selectMe);
  const isAuthorized = useSelector(selectIsAuthorized);
  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER']);
  const isBuyerStaff = useDefineUserRole(me, ['ROLE_BUYER_STAFF']);
  const { formik, loading } = useFormikCompany(me?.company, countryData);
  const { values, touched, handleChange, errors, setFieldValue, handleBlur, handleSubmit } =
    formik;

  useEffect(() => {
    if (me?.company?.country?.id) {
      setCountryId(me?.company?.country?.id);
    }
  }, [me]);

  useEffect(() => {
    makeRequest('/api/phone_codes');
  }, []);

  return (
    <>
      <div className={classes.totalCard}>
        <div className={classes.containerForm}>
          {!isAuthorized ? (
            <b className={classes.title}>{fm('UNAUTHORIZED.YOUR.COMPANY')}</b>
          ) : (
            <>
              {isBuyerStaff && !me?.company ? (
                <b className={classes.title}>{fm('BUYER.STARFF.YOUR.COMPANY')}</b>
              ) : (
                <>
                  <b className={classes.title}>{fm('YOUR.COMPANY')}</b>
                  <TextField
                    type='text'
                    label={fm('COMPANY.FORM.LEGAL_PERSON')}
                    margin='normal'
                    name='name'
                    value={values.name}
                    variant='outlined'
                    disabled={!isBuyer}
                    onChange={handleChange}
                    helperText={errors.name}
                    error={!!errors.name}
                  />
                  <TextField
                    type='text'
                    label={fm('COMPANY.FORM.INN')}
                    margin='normal'
                    name='inn'
                    value={values.inn}
                    variant='outlined'
                    disabled={!isBuyer}
                    onChange={handleChange}
                    helperText={errors.inn}
                    error={!!errors.inn}
                  />
                  <div className={classes.dates}>
                    <CountrySelect
                      label={fm('USER.INPUT.COUNTRY_CODE')}
                      countries={countries}
                      countryId={countryId}
                      setCountryId={(id: number) => {
                        setCountryId(id);
                        setFieldValue('phone_number', '');
                      }}
                      countriesLoading={countriesLoading}
                      disabled={!isBuyer}
                      showCode
                      variant='outlined'
                    />
                    {countryData && (
                      <IMaskPhoneInput
                        mask={countryData.mask}
                        onBlur={handleBlur}
                        value={values.phone_number}
                        onChange={(e: any) => {
                          setFieldValue(
                            'phone_number',
                            e.target.value ? e.target.value.replace(/[^0-9]/g, '') : ''
                          );
                        }}
                        type='tel'
                        label={fm('AUTH.INPUT.PHONE')}
                        margin='normal'
                        className='kt-width-full'
                        name='phone_number'
                        helperText={touched.phone_number && errors.phone_number}
                        error={Boolean(touched.phone_number && errors.phone_number)}
                        variant='outlined'
                        disabled={!isBuyer}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              {' '}
                              +{countryData.code}{' '}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    {isBuyer && (
                      <ButtonWithLoader
                        marginTop={27}
                        fullWidth
                        disabled={loading}
                        loading={loading}
                        onPress={handleSubmit}
                      >
                        {fm('COMMON.BUTTON.SAVE')}
                      </ButtonWithLoader>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
